import {Feature, View,} from "ol";
import TileLayer from "ol/layer/Tile";
import ImageLayer from "ol/layer/Image";
import TileImage from 'ol/source/TileImage';
import TileGrid from 'ol/tilegrid/TileGrid';
import {
	fullColorHex,
	getPrefixedUrlSecure,
	getSuffixUrl,
	hsvToRgb,
	isNull,
	isSuperuser,
	notNull,
	round
} from '../../../utils/utils';
import Static from "ol/source/ImageStatic";
import {
	AnnotationsConstants,
	componentIds,
	ErrorMessage,
	ErrorTypes,
	mapLayerTypes,
	responsiveLayers,
	StitchDirection,
	TileViewerConstants,
	tileViewerPlotColors
} from "../../../utils/const";
import {store} from "../../../helper/store";
import VectorLayer from "ol/layer/Vector";
import Select from "ol/interaction/Select";
import VectorSource from "ol/source/Vector";
import {styleFunction} from "./annotations_app_utils";
import {getCenter} from "ol/extent";
import GridReference from "ol-ext/control/GridReference";
import Polygon, {fromExtent} from "ol/geom/Polygon";
import Projection from "ol/proj/Projection";
import Graticule from "ol-ext/control/Graticule";
import Circle from "ol/geom/Circle";
import LineString from "ol/geom/LineString";
import {Fill, Stroke, Style, Text} from "ol/style";
import {updateLastImageRenderTime} from "../../../redux_actions/gamma.state.action";
import {message} from "antd";
import {AuthHeader} from "../../../configs/auth.token";
import {urls} from "../../../utils/urls";
import {notifyError} from "./display.notification";
import {getAnnotationExtent} from "../openseadragon/annotations/annotationGeometry";

/** False for the OpenSeadragon viewer, which only stands in for a few OL map methods. */
export const isOpenLayersMap = (slidemap) =>
	!!slidemap &&
	typeof slidemap.getView === 'function' &&
	typeof slidemap.getLayers === 'function' &&
	typeof slidemap.getControls === 'function' &&
	!slidemap._isOpenSeadragon;


function ProgressTracker(updateState) {
	this.updateState = updateState
	this.loading = 0
	this.loaded = 0
	this.tilesLoadingCount = 0
	this.tilesErrorCount = 0
  }

ProgressTracker.prototype.addTilesLoadingCount = function () {
	++this.tilesLoadingCount;
}

ProgressTracker.prototype.minusTilesLoadingCount = function () {
	--this.tilesLoadingCount;
}

ProgressTracker.prototype.addTilesErrorCount = function () {
	++this.tilesErrorCount;
}

  ProgressTracker.prototype.addLoading = function () {
	++this.loading;
	this.update();
  };

  ProgressTracker.prototype.addLoaded = function () {
	if (this.loaded < this.loading) ++this.loaded
	this.update()
  };

  ProgressTracker.prototype.update = function () {
	  const progress = (this.loading > 0) ? ((this.loaded / this.loading) * 100) : 101
	  this.updateState(progress)
  };

  ProgressTracker.prototype.show = function () {
	  this.loaded = 0
	  this.loading = 0
	  this.updateState(0)
  };

  ProgressTracker.prototype.hide = function () {
	  this.loaded = 0
	  this.loading = 0
	  if (this.tilesLoadingCount === 0 || this.tilesLoadingCount !== this.tilesErrorCount) {
		  this.updateState(101)
	  }
  };

export const updateProgress = (source, progress) => {
	if (source.progressTrackingEnabled) return

	source.on('tileloadstart', function () {
		progress.addTilesLoadingCount()
		progress.addLoading();
	});
	source.on('tileloaderror', function () {
		progress.addTilesErrorCount();
	});
	source.on('tileloadend', function () {
		progress.minusTilesLoadingCount();
        isSuperuser() && store.dispatch(updateLastImageRenderTime(new Date().getTime()))
        progress.addLoaded();
	});
	source.progressTrackingEnabled=true;

}
export const connectProgressTracker = (map, updateState) => {
    if (typeof updateState !== 'function') return

	let progressTracker = new ProgressTracker(updateState)

	if (map.progressTrackingEnabled) {
		console.error("Not tested for multiple maps")
	}

	map.on('loadstart', () => progressTracker.show())
	map.on('rendercomplete', () => progressTracker.hide())

	map.getLayers().on("propertychange", function(e) {
		map.getLayers().forEach((layer)=>{
			updateProgress(layer.getSource(), progressTracker);
		});
	});

	map.getLayers().forEach((layer)=>{
		updateProgress(layer.getSource(), progressTracker);
	});

	map.progressTrackingEnabled=true;
}

export const imageLoader = (tile, src, slide_id, map_id) => {
	const dicom = store.getState().mapsStateReducer[map_id]?.is_dicom;
	if (dicom) {
		const maxZoomLevel = store.getState().mapsStateReducer[map_id]?.zoomLevels?.length-1
		src = urls.GET_DICOM_TILE_DATA + '?slide_id=' + slide_id + '&src=' + src + '&maxZoomLevel=' + maxZoomLevel //+ '&prof';
	}
	const xhr = new XMLHttpRequest();
	xhr.open('GET', src);
	// Authorization header not working with s3 buckets on cloud
	// It is made to create user level authorization for images
	if (dicom) {
		// TODO: remove authorization if fetching from s3 bucket
		xhr.setRequestHeader('Authorization', AuthHeader());
	} else {
		xhr.setRequestHeader('Accept', 'image/*');
	}
	xhr.withCredentials = true;
	xhr.responseType = "arraybuffer";
	xhr.onload = function () {
		let arrayBuffer = new Uint8Array(this.response);

		if (this.status === 500) {
			var enc = new TextDecoder("utf-8");
			var responseBody = enc.decode(arrayBuffer);
			responseBody.includes("nginx") && responseBody.includes(ErrorTypes.NGINX_ERROR) &&
			notifyError("nerror", ErrorMessage.NGINX_ERROR, false, 0, true);
		}

		// images accessed through an aws cloudfront distribution
		// return with a 403 HTTP status code if signed cookies aren't proper
		if (this.status === 403) {
			var enc = new TextDecoder("utf-8");
			var responseBody = enc.decode(arrayBuffer);
			console.debug(responseBody);
			if (responseBody.includes("403 Forbidden")) {
				message.warn("Access to images has expired. Refresh page to continue viewing scan");
			}
			// sample response: <?xml version="1.0" encoding="UTF-8"?><Error><Code>AccessDenied</Code><Message>Access denied</Message></Error>
			// sample response for file not present in s3 bucket:
			// <?xml version="1.0" encoding="UTF-8"?>
			// <Error><Code>AccessDenied</Code><Message>Access Denied</Message>
			// <RequestId>J4TZ1AZBGKXVZRVB</RequestId>
			// <HostId>Og+DPKV71mtifl5YRWfYCVwW6uHuJTyaFrfFgBAAjRn/mCsM6l3PRIc9EgrwrSa/iOs/A3hNdfc=</HostId>
			//</Error>
			if (responseBody.includes("<Error>") && !responseBody.includes("<RequestId>")) {
				console.error("Access to images has expired. Refresh page to continue viewing scan");
				message.warn("Access has expired. Refresh page to continue viewing scan");
			}
			if (responseBody.includes("<RequestId>")) {
				// If you write your own tileLoadFunction, 
				// it is important to set the state correctly to ERROR when the tile cannot be loaded.
				// Otherwise the tile cannot be removed from the tile queue and will block other requests.
				// 3 is ERROR state
				tile.setState(3);
			}
			return;
		}

		let blob = new Blob([arrayBuffer], {type: 'image/png'});
        tile.getImage().src = (window.URL || window.webkitURL).createObjectURL(blob);
    };
	xhr.addEventListener('error', function () {
		// If you write your own tileLoadFunction, 
		// it is important to set the state correctly to ERROR when the tile cannot be loaded.
		// Otherwise the tile cannot be removed from the tile queue and will block other requests.
		// 3 is ERROR state
		tile.setState(3);
	});
    xhr.send();
}

export const getTileSize = (slide, stackMode) =>
	stackMode ? [slide.tile_width, slide.tile_height] :
		[slide.stitched_tile_width, slide.stitched_tile_height];

export const getImageShape = (slide, tiled) => {
	if (slide.specimen_type === "preview")
		return [slide.x_step * slide.x_fields, slide.y_step * slide.y_fields];
	else if (tiled)
		return [slide.uperpixel * slide.tile_width * slide.x_fields,
			slide.uperpixel * slide.tile_height * slide.y_fields];
	return [slide.stitched_tile_width * slide.stitched_x_max, slide.stitched_tile_height * slide.stitched_y_max];
}

// TODO: refactor this to send only required data
export const getViewLevelsInfo = (slide) => {
	let splitStitchedLevels = slide.z_levels?.split(',');
	if (isNull(splitStitchedLevels)) {
		return {lastStitchedLevel: -1, numberOfTiledLevels: -1, lastTiledLevel: -1, smallestTiledLevel: -1}
	}
	let lastStitchedLevel = parseInt(splitStitchedLevels[splitStitchedLevels.length - 1]);
	let tiled_levels = Array.from(Array(lastStitchedLevel > 2 ? lastStitchedLevel - 2 : 0).keys());
	let smallestTiledLevel = slide.smallest_tiled_level;
	let splitTiledLevels = slide.tiling_z_levels != null ? slide.tiling_z_levels.split(',') : tiled_levels;
	let lastTiledLevel = parseInt(splitTiledLevels[splitTiledLevels.length - 1]);
	let numberOfTiledLevels = lastTiledLevel - smallestTiledLevel + 1;
	return {lastStitchedLevel, numberOfTiledLevels, lastTiledLevel, smallestTiledLevel};
}

export const getFullScanShapeInPixels = (slide) => {
	let imageWidth, imageHeight;
	if (slide.specimen_type === "preview") {
		imageWidth = slide.x_step * slide.x_fields;
		imageHeight = slide.y_step * slide.y_fields;
	} else {
		imageWidth = slide.stitched_tile_width * slide.stitched_x_max;
		imageHeight = slide.stitched_tile_height * slide.stitched_y_max;
	}
	return [imageWidth, imageHeight];
}

export const getImageInfo = (slide) => {
	if (JSON.stringify(slide.txty) === JSON.stringify({}))
		return {};
	try {
		let imageSizes = [[slide.tile_width, slide.tile_height]];
		let splitTiledLevels = (slide.tiling_z_levels || '').split(',');
		let lastTiledLevel = parseInt(splitTiledLevels[splitTiledLevels.length - 1]);
		let imageInfo = {
			xcount: slide.x_fields,
			ycount: slide.y_fields,
			zcount: lastTiledLevel + 1,
			totalImages: slide.total_images
		}
		for (let i = 1; i < imageInfo.zcount; i++) {
			imageSizes.push([
				Math.floor(imageSizes[i - 1][0] / 2),
                Math.floor(imageSizes[i - 1][1] / 2),
            ]);
        }
        imageSizes.reverse();
        imageInfo.imageSizes = imageSizes;

        return imageInfo;
    } catch (e) {
        return {};
    }
}

export const isZoomForTiled = (zoom, viewLevelsInfo) => viewLevelsInfo.smallestTiledLevel !== -1 &&
    Math.round(zoom) > (viewLevelsInfo.lastStitchedLevel - viewLevelsInfo.numberOfTiledLevels);

/** Field extents in `viewport` (OL extent, y up), keyed "xi|yi", the same selection as getImageLayers. */
export const getVisibleTilesForExtent = (viewport, slide, imageInfo, maxZoom) => {
	const posList = {};
	const mstCountMap = {};
	const baseCanvasY = getFullScanShapeInPixels(slide)[1];
	const [imageWidth, imageHeight] = imageInfo.imageSizes[maxZoom];
	const txtyInfo = slide.txty || {};

	for (let image in txtyInfo) {
		const xi = txtyInfo[image]["x_index"];
		const yi = txtyInfo[image]["y_index"];
		const posInfo = txtyInfo[image]["pos"];

		const xleft = posInfo['X'];
		const xright = posInfo['X'] + imageWidth;
		const yleft = baseCanvasY - (posInfo['Y'] + imageHeight);
		const yright = baseCanvasY - posInfo['Y'];

		if (!isNaN(xleft) && xright > viewport[0] && xleft < viewport[2] && yright > viewport[1] && yleft < viewport[3]) {
			const key = xi + "|" + yi;
			posList[key] = [Math.round(xleft * 10) / 10, Math.round(yleft * 10) / 10,
				Math.round(xright * 10) / 10, Math.round(yright * 10) / 10];
			mstCountMap[key] = posInfo['displayOrder'];
		}
	}

	return {posList, mstCountMap};
};

export const getImageLayers = (zoom, imageInfo, slide, txtyInfo, viewport, maxZoom, zStackLevel, minZLevel, maxZLevel,
                               takeZStack, currentExposure, mapId) => {
	let imageType = slide.img_type;
	let posList = {};
	let mstCountMap = {};
	let layers = [];

	// let imageShape = getImageShape(slideData)
	const fullScanShapePxls = getFullScanShapeInPixels(slide);
	let baseCanvasY = fullScanShapePxls[1];

	if (!Number.isInteger(fullScanShapePxls[1])) {
		console.error("11 - imageShape[1]", fullScanShapePxls[1]);
		console.error("11 - full scan height is not an integer, FIX this to avoid viewer side stitching artifacts");
	}

	let minY = baseCanvasY;

	let xKey = 'X';
	let yKey = 'Y';
	let mstCountKey = 'displayOrder';

	for (let image in txtyInfo) {
		let xi = txtyInfo[image]["x_index"];
		let yi = txtyInfo[image]["y_index"];

		let posInfo = txtyInfo[image]["pos"];

		const imageWidth = imageInfo.imageSizes[maxZoom][0];
		const imageHeight = imageInfo.imageSizes[maxZoom][1];
		// NOTE: Number.isInteger is known to return false positives for numbers with large magnitude
		// eg: Number.isInteger(5.0000000000000001); returns true, because of loss of precision
		if (!Number.isInteger(imageWidth) || !Number.isInteger(imageHeight)) {
			console.error("11 - image width and height are not integers, FIX this to avoid viewer side stitching artifacts");
		}

		let xleft = posInfo[xKey];
		let xright = (posInfo[xKey] + imageInfo.imageSizes[maxZoom][0]);
		let yleft = baseCanvasY - (posInfo[yKey] + imageInfo.imageSizes[maxZoom][1]);
		let yright = baseCanvasY - (posInfo[yKey]);

		if (!Number.isInteger(xleft) || !Number.isInteger(yright)) {
			console.error("11 - xleft", xleft, "yright", yright);
			console.error("image extents are not integers, this is known to cause relative shift of adjacent images for tiled levels");
			console.error("and thus resulting in exaggeration of existing and introduction of new stitching artifacts in the viewer");
			console.error("FIX this and use integer extents immediately!");
		}

		if (!isNaN(xleft)) {
			if (xright > viewport[0] && xleft < viewport[2] && yright > viewport[1] && yleft < viewport[3]) {
				posList[xi + "|" + yi] = [Math.round(xleft * 10) / 10, Math.round(yleft * 10) / 10, Math.round(xright * 10) / 10, Math.round(yright * 10) / 10];
				mstCountMap[xi + "|" + yi] = posInfo[mstCountKey];
				if (minY > yleft)
					minY = yleft;
			}
		}
	}

    for(let pos in posList) {
        let posI = pos.split("|")[0];
        let posJ = pos.split("|")[1];
        if (takeZStack) {
            for(let i = minZLevel; i <= maxZLevel; i++) {
	            let urlSuffix = getSuffixUrl(slide, true, i);

				urlSuffix = getPrefixedUrlSecure(urlSuffix, slide);

	            const imageLayer = new ImageLayer({
		            source: new Static({
			            imageExtent: posList[pos],
			            url: `${urlSuffix}/${zoom}/x${posI}y${posJ}.${imageType}`,
			            imageSize: imageInfo.imageSizes[zoom],
			            imageLoadFunction: (image, src) => imageLoader(image, src, slide.id, mapId),
			            crossOrigin: 'anonymous',
		            }),
		            posI: posI,
		            posJ: posJ,
		            posZ: zoom,
		            posS: i,
		            zIndex: mstCountMap[pos],
		            name: mapLayerTypes.TILED_LAYER_NAME,
		            visible: i === zStackLevel,
	            });

	            layers.push(imageLayer);
            }
        } else {
	        (slide.meta?.exposure_stack || [currentExposure]).forEach(exposure => {
		        let urlSuffix = getSuffixUrl(slide, true, 0, exposure);
		        urlSuffix = getPrefixedUrlSecure(urlSuffix, slide);
				
		        const imageLayer = new ImageLayer({
			        source: new Static({
				        imageExtent: posList[pos],
				        url: `${urlSuffix}/${zoom}/x${posI}y${posJ}.${imageType}`,
				        imageSize: imageInfo.imageSizes[zoom],
				        imageLoadFunction: (image, src) => imageLoader(image, src, slide.id, mapId),
				        crossOrigin: 'anonymous',
			        }),
			        posI: posI,
			        posJ: posJ,
			        posZ: zoom,
			        posS: 0,
			        exposure,
			        zIndex: mstCountMap[pos],
			        name: mapLayerTypes.TILED_LAYER_NAME,
			        visible: exposure === currentExposure,
		        });
		        layers.push(imageLayer);
	        });
        }
    }

    return layers;
}

export const getProjection = (imageShape, unitPerPixel) => new Projection({
	code: 'MORPHLE',
	units: unitPerPixel === 1 ? 'microns' : 'pixels',
	extent: [0, 0, imageShape[0], imageShape[1]],
	metersPerUnit: unitPerPixel / 1e6,
	global: true,
	getPointResolution: (resolution) => resolution,
});

export const getView = (slide, mapState, projection, resolutions) =>
	new View({
		projection: projection,
		extent: projection.getExtent(),
		center: mapState.x === -1 ? getCenter(projection.getExtent()) : [mapState.x, mapState.y],
		zoom: mapState.z,
		minZoom: Math.max(0, resolutions.length - 8),   // Allow only 8 zoom levels
		maxZoom: resolutions.length - getMaxZoomOffset(slide, mapState.digitalZoomStatus),
		maxResolution: resolutions[0] / getZoomRatio(slide),
		constrainResolution: true,      // force integer zoom levels
		constrainOnlyCenter: true,
	});

export const getStitchedLayers = (slide, mapId, projection, currentExposure) => {
	let numberOfTiledLevels = getViewLevelsInfo(slide)['numberOfTiledLevels'];
	let tileSize = getTileSize(slide, false);
	let resolutions = getResolutions(getZoomLevels(slide));
	resolutions = slide.smallest_tiled_level > 0 ? resolutions.slice(0, -numberOfTiledLevels) : resolutions;

	return (slide?.meta?.exposure_stack || [currentExposure]).map(exposure =>
		getLayer(slide?.id, mapId, tileSize, projection, resolutions,
			getPrefixedUrlSecure(getSuffixUrl(slide, false, 0, exposure), slide), exposure,
			exposure === currentExposure));
}

export const getLayer = (slide_id, mapId, tileSize, projection, resolutions, url, exposure, visible) =>
    new TileLayer({
        extent: projection.getExtent(),
		preload: Infinity,
        source: new TileImage({
            tileGrid: new TileGrid({
                extent: projection.getExtent(),
                origin: [0, projection.getExtent()[3]],
                resolutions: resolutions,
                tileSize: tileSize,
            }),
			tileLoadFunction: (tile, src) => imageLoader(tile, src, slide_id, mapId),
			// projection: projection,
			url: url,
			wrapX: false,
			crossOrigin: 'anonymous',
		}),
		name: mapLayerTypes.STITCHED_LAYER_NAME,
		exposure,
		visible,
	});

export const getTileLayer = (id, tileSize, projection, resolutions, url, extent) =>
	new TileLayer({
		id,
		extent: extent,
		preload: Infinity,
		source: new TileImage({
			tileGrid: new TileGrid({
				extent,
				origin: [0, extent[3]],
				resolutions: resolutions,
				tileSize: tileSize,
			}),
			url: url,
			wrapX: false,
			crossOrigin: 'anonymous',
		}),
		visible: false,
		name: mapLayerTypes.STITCHED_LAYER_NAME,
	});

export const locateAnnotationOnMap = (slidemap, annotation, is_blood) => {

	if (isNull(slidemap)) return;

	if (slidemap._isOpenSeadragon) {
		if (!annotation) return;
		if (is_blood) return slidemap.getView().animate({center: JSON.parse(annotation.center)});
		return slidemap.getView().fit(getAnnotationExtent(annotation));
	}

	let vectorLayers = slidemap.getLayers().array_.filter(layer => layer instanceof VectorLayer);
	for (let vector of vectorLayers) {
		if (vector.getSource().getFeatures().some(feature_ => notNull(feature_.getId())) && annotation) {
			let selectedFeature = vector.getSource().getFeatures().find(feature_ =>
				feature_.getId() === annotation.id);
			if (selectedFeature) {
				if (is_blood) {
					getStyleForBloodAnnotation(selectedFeature);
					return slidemap.getView().animate({center: JSON.parse(annotation.center)});
				} else {
					return slidemap.getView().fit(selectedFeature.getGeometry());
				}
			}
		}
	}
}

export const getZoomLevels = (slide) => slide.z_levels?.split(",").map(level => parseInt(level)) || [];

export const getTilingZoomLevels = (slide) => slide.tiling_z_levels.split(",").map(level => parseInt(level));

export const getResolutions = (zoomLevels) =>
	zoomLevels.map((level) => Math.pow(2, parseInt(level))).reverse();

export const getResolutionsForMicron = (zoomLevels, uperpixel) =>
	getResolutions(zoomLevels).map(resolution => resolution * uperpixel);

export const getZoomRatio = slide =>
	(store.getState().morpheusSettingReducer?.viewer_configurations?.zoomRatio || 1.0) * (slide?.view_scale || 1.0);

export const getZoomScale = (objective_type, zoomLevels) => {
	let maxZoomScale = getMaxZoomScale(objective_type);
	return zoomLevels.map((zoom, idx) => round(maxZoomScale / Math.pow(2, zoomLevels.length - idx - 1), 2));
}

export const getMaxZoomScale = (objective_type) =>
    objective_type === "hundred_x" ? 200 : objective_type === "twenty_x" ? 40 : objective_type === "sixty_x" ? 120 : 80;

/**
 * Imported WSIs (TIFF/NDPI/SVS) store native-resolution tiles at the top level, unlike
 * Morphle scans whose top level is only shown with digital zoom.
 */
export const isImportedSlide = (slide) => slide?.bucket_name === "imported_scans";

/** Levels below zoomLevels.length that the view may not exceed (maxZoom = length - offset). */
export const getMaxZoomOffset = (slide, digitalZoomStatus) =>
	(isImportedSlide(slide) ? 0 : 1) + (digitalZoomStatus ? 0 : 1);

export const getMaxViewZoom = (slide, zoomLevels, digitalZoomStatus) =>
	zoomLevels.length - getMaxZoomOffset(slide, digitalZoomStatus);

/** Highest zoom index that shows real (non-upsampled) tiles. */
export const getNativeTopZoom = (slide, zoomLevels) =>
	zoomLevels.length - (isImportedSlide(slide) ? 1 : 2);

const _OBJECTIVE_NOMINAL = {twenty_x: 20, forty_x: 40, sixty_x: 60, hundred_x: 100};

/** Nominal objective for a native MPP, binned like the importer (≥0.375 → 20×, ≥0.15 → 40×, else 100×). */
export const nominalObjectiveForMpp = (mpp) => {
	const m = Number(mpp);
	if (!(m > 0)) return null;
	return m >= 0.375 ? 20 : m >= 0.15 ? 40 : 100;
};

/**
 * Magnification label per zoom index. Imported slides label the native top level with their
 * objective and get one extra (digital, 2× upsampled) entry above it; Morphle scans keep
 * objective labels.
 */
export const getZoomScaleForSlide = (slide, zoomLevels) => {
	if (!isImportedSlide(slide)) return getZoomScale(slide?.objective_type, zoomLevels);
	const nominal = _OBJECTIVE_NOMINAL[String(slide?.objective_type || "").toLowerCase()]
		|| nominalObjectiveForMpp(slide?.uperpixel);
	if (!nominal) return getZoomScale(slide?.objective_type, zoomLevels);
	const n = zoomLevels.length;
	return Array.from({length: n + 1}, (_, idx) => round(nominal / Math.pow(2, n - 1 - idx), 2));
};

export const removeAllVectorLayers = (slidemap) => slidemap &&
	slidemap.getLayers().array_.filter(layer => layer instanceof VectorLayer).forEach(layer =>
		slidemap.removeLayer(layer));

export const removeAllSelectInteractions = (slidemap) => slidemap &&
	slidemap.getInteractions().array_.filter(interaction => interaction instanceof Select).forEach(
		interaction => slidemap.removeInteraction(interaction));

export const clearSelectFeatures = (slidemap) => slidemap &&
	slidemap.getInteractions().array_.filter(interaction => interaction instanceof Select).forEach(interaction =>
		interaction.getFeatures().clear());

export const setActiveAllSelectInteractions = (slidemap, active) => slidemap &&
	slidemap.getInteractions().getArray().filter(interaction => interaction instanceof Select).forEach(interaction =>
		interaction.setActive(active));

export const createVectorLayer = (map, layer) => {
	let vector = new VectorLayer({
		source: new VectorSource({}),
		...layer,
	});
	vector.setZIndex(1000000);
	map.addLayer(vector);
	vector.setStyle(layer.styleFunction || styleFunction);
	layer.vector = vector;
	return vector;
}

export const animateToState = (view, mapState) => mapState &&
	view.animate({
		center: [mapState.x || view.getCenter()[0], mapState.y || view.getCenter()[1]],
		zoom: notNull(mapState.z) ? mapState.z : view.getZoom(),
		rotation: notNull(mapState.r) ? mapState.r : view.getRotation(),
	});

export const removeGridControls = slidemap => {
	if (!isOpenLayersMap(slidemap)) return;
	slidemap.getControls().getArray()
		.filter(control => control instanceof GridReference || control instanceof Graticule)
		.forEach(control => slidemap.removeControl(control));
}


export const getRotatedExtent = (extent, rotation) => {
	let center = getCenter(extent);
	let geom = fromExtent(extent);
	geom.rotate(rotation, center);
	return geom.getExtent();
}

export const getHighestFocusPoint = (tiltFocusUnits, islandData, tiltInfo) => {
	let highestPoint = null;
	// tilt focus units
	for (const focusUnit of tiltFocusUnits) {
		if (focusUnit) {
			if (highestPoint === null || focusUnit['focusedDown'] > highestPoint) {
				highestPoint = focusUnit['focusedDown'];
			}
		}
	}
	// county focus units (both takes)
	for (let i = 0; i < islandData.length; i++) {
		let countyData = islandData[i]["counties"];
		for (let idx = 0; idx < countyData.length; idx++) {
			let focusList = countyData[idx]['focusList'];
			let dualFocusList = countyData[idx]['dualFocusList'];
			if (dualFocusList) {
				focusList = focusList.concat(dualFocusList);
			}
			if (focusList) {
				for (let j = 0; j < focusList.length; j++) {
					if (focusList[j]['focusedAt']) {
						if (highestPoint === null || focusList[j]['focusedDown'] > highestPoint) {
							highestPoint = focusList[j]['focusedDown'];
						}
					}
				}
			}
		}
	}
	// adjacent focus units (for wsi 3.0 and version 2.8)
	try {
		for (const countyTiltInfo of tiltInfo) {
			const adjacentFU = countyTiltInfo['focusUnit'];
			if (adjacentFU === null || adjacentFU === undefined) {
				continue;
			}
			let motorPos = adjacentFU['focusedAt'];
			if (motorPos !== undefined && motorPos !== null) {
				if (highestPoint === null || adjacentFU['focusedDown'] > highestPoint) {
					highestPoint = adjacentFU['focusedDown'];
				}
			}
		}
	} catch (error) {
		console.error("error while using county tilt pts", error);
	}
	return highestPoint;
}

export const getBoundsForRectAroundCentre = (tileWidth, tileHeight, uperpixel, x, y, offFromCentre) => [
	[(x + 0.5 - offFromCentre) * (tileWidth * uperpixel), (y + 0.5 - offFromCentre) * (tileHeight * uperpixel)],
	[(x + 0.5 + offFromCentre) * (tileWidth * uperpixel), (y + 0.5 - offFromCentre) * (tileHeight * uperpixel)],
	[(x + 0.5 + offFromCentre) * (tileWidth * uperpixel), (y + 0.5 + offFromCentre) * (tileHeight * uperpixel)],
	[(x + 0.5 - offFromCentre) * (tileWidth * uperpixel), (y + 0.5 + offFromCentre) * (tileHeight * uperpixel)],
	[(x + 0.5 - offFromCentre) * (tileWidth * uperpixel), (y + 0.5 - offFromCentre) * (tileHeight * uperpixel)],
];

const get_styling_from_params = (strokeWidth, color, fill, title, textColor, textSize) => {
	return new Style({
		stroke: new Stroke({
			width: strokeWidth,
			color: color
		}),
		fill: fill ? new Fill({
			color: color
		}) : null,
		text: new Text({
			text: title,
			font: textSize ? `${textSize}px sans-serif` : '16px sans-serif',
			fill: new Fill({
				color: textColor,
			}),
		})
	})
}

export const getStyleForBloodAnnotation = (feature) => feature.setStyle(new Style({
	fill: new Fill({
		color: 'rgba(0, 0, 0, 0.0)'
	}),
	stroke: new Stroke({
		color: '#db0fa8',
		width: 4
	}),
	zIndex: 1
}));

export const resetAnnotationStyle = () => {
	const activeMapId = store.getState().gammaStateReducer.activeMapId;
	const mapState = store.getState().mapsStateReducer[activeMapId];
	const annotation = mapState.annotationState?.markedAnnotation;
	if (notNull(annotation) && notNull(mapState.slidemap)) {

		let baseStyle = new Style({
			stroke: new Stroke({
				color: annotation.color ? annotation.color : AnnotationsConstants.DEFAULT_ANNO_COLOR,
				width: 2
			}),
			fill: new Fill({
				color: 'rgba(0, 0, 0, 0.0)'
			}),
			zIndex: 0
		});

		let otherStyle = new Style({
			stroke: new Stroke({
				color: "white",
				width: 5
			}),
		});

		let vectorLayers = mapState.slidemap.getLayers().array_.filter(layer => layer instanceof VectorLayer);
		for (let vector of vectorLayers) {
			let selectedFeature = vector.getSource().getFeatures().find(feature_ =>
				feature_.getId() === annotation.id);
			if (selectedFeature) {
				selectedFeature.setStyle([otherStyle, baseStyle]);
			}
		}
	}
}

export const drawCircle = (x, y, radius, vectorSource, title, strokeWidth, color, label = "", textColor, textSize = 16) => {
	drawCircleFill(x, y, radius, vectorSource, title, false, strokeWidth, color, label, textColor, textSize);
}

export const drawCircleFill = (x, y, radius, vectorSource, title, fill, strokeWidth, color, label = "", textColor, textSize = 16) => {
	let feature = new Feature({
		geometry: new Circle([x, y], radius),
		name: "circle",
		title, label,
	});
	feature.setStyle(get_styling_from_params(strokeWidth, color, fill, title, textColor, textSize));
	vectorSource.addFeature(feature);
}

export const plotSquare = (data, slideData, slideMeta, vectorSource, color, size, strokeWidth) => {
	if(data !== null){
		let motorX = data.xPosition;
		let motorY = data.yPosition;
		let imagePositions = mapMotorToCanvas(motorX, motorY, slideData, slideMeta);
		let x = imagePositions.x;
		let y = imagePositions.y;
		let side = size * (slideData.tile_height * slideData.uperpixel) / 4;
		let bounds = getSquareBounds(x, y, side);
		drawRectangle(bounds, vectorSource, false, strokeWidth, color);
	}
}

export const getSquareBounds = (x, y, side) => {
	return [
		[x - (side / 2), y - (side / 2)],
		[x + (side / 2), y - (side / 2)],
		[x + (side / 2), y + (side / 2)],
		[x - (side / 2), y + (side / 2)],
		[x - (side / 2), y - (side / 2)]
	]
}

export const drawTriangle = (bounds, vectorSource, fill, strokeWidth, color, label = "", textColor) => {
	let feature = new Feature({
		geometry: new Polygon([bounds]),
		name: "polygon",
		title: "",
		label,
	});
	feature.setStyle(get_styling_from_params(strokeWidth, color, fill, "", textColor));
	vectorSource.addFeature(feature);
}

export const drawRectangle = (bounds, vectorSource, fill, strokeWidth, color, label = "", textColor) => {
	let feature = new Feature({
		geometry: new Polygon([bounds]),
		name: "polygon",
		title: "",
		label,
	});
	feature.setStyle(get_styling_from_params(strokeWidth, color, fill, "", textColor));
	vectorSource.addFeature(feature);
}

export const drawLine = (x, y, length, vectorSource, title, strokeWidth, color, label = "", textColor, textSize) => {
	let feature = new Feature({
		geometry: new LineString([[x, y], [x + length, y]]),
		name: "line",
		title,
		label,
	});
	feature.setStyle(get_styling_from_params(strokeWidth, color, false, `${title}`, textColor, textSize));
	vectorSource.addFeature(feature);
}

export const drawVector = (x, y, vx, vy, vectorSource, color, strokeWidth = 2, title = "", textColor) => {
	// arrow from (x, y) to (x + vx, y + vy). map-y points down, so a positive vy
	// (image/sensor downward residual) draws downward, matching the slide.
	const tipX = x + vx, tipY = y + vy;
	let shaft = new Feature({
		geometry: new LineString([[x, y], [tipX, tipY]]),
		name: "line", title, label: "",
	});
	shaft.setStyle(get_styling_from_params(strokeWidth, color, false, "", textColor));
	vectorSource.addFeature(shaft);

	const len = Math.sqrt(vx * vx + vy * vy);
	if (len < 1e-9) return;
	const ang = Math.atan2(vy, vx);
	const head = Math.max(len * 0.35, 1e-9);
	const a1 = [tipX - head * Math.cos(ang - Math.PI / 6), tipY - head * Math.sin(ang - Math.PI / 6)];
	const a2 = [tipX - head * Math.cos(ang + Math.PI / 6), tipY - head * Math.sin(ang + Math.PI / 6)];
	let arrowHead = new Feature({
		geometry: new LineString([a1, [tipX, tipY], a2]),
		name: "line", title: "", label: "",
	});
	arrowHead.setStyle(get_styling_from_params(strokeWidth, color, false, "", textColor));
	vectorSource.addFeature(arrowHead);
}

export const mapMotorToCanvas = (xMotorPosition, yMotorPosition, slideData, slideMeta) => {
	let xImagePos = ((parseInt(xMotorPosition) - slideData.x_min + (slideData.x_step / 2)) / slideData.x_step);
	let yImagePos = ((parseInt(yMotorPosition) - slideData.y_min + (slideData.y_step / 2)) / slideData.y_step);

	return imagetoCanvasPosHelper(slideMeta, slideData, xImagePos, yImagePos);
}

export const imagetoCanvasPosHelper = (slideMeta, slideData, xImagePos, yImagePos) => {
	let x, y;
	if (slideMeta.device_settings['scanConfig']['flipXYDuringStitching']) {
		x = (yImagePos) * (slideData.tile_width * slideData.uperpixel);
		y = (xImagePos) * (slideData.tile_height * slideData.uperpixel);
	}

	else {
		x = (slideData.x_fields - xImagePos) * (slideData.tile_width * slideData.uperpixel);
		y = (slideData.y_fields - yImagePos) * (slideData.tile_height * slideData.uperpixel);
	}

	return {x: x, y: y};
}

export const plotFftFourDAreaUnit = (v, slideData, x, y, margin, sizeOfMarker, vectorSource, mindiff, maxdiff, textColor) => {

	y = slideData.y_fields - y - 1;

	// NORTH MARKERS
	if (v["NorthDiff"]) {
		let numCrops = v["NorthDiff"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			let hue = 180 - Math.floor(180 * ((v["NORTH"][crop] - mindiff) / (maxdiff - mindiff)));
			let rgbColor = hsvToRgb(hue / 360, 1, 1);
			let color = fullColorHex(rgbColor.r, rgbColor.g, rgbColor.b);
			let xStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
			let xEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;
			let bounds = [
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y + 1) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y + 1) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y - (margin) + 1) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y - (margin) + 1) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y + 1) * (slideData.tile_height * slideData.uperpixel)]
			];
			drawRectangle(bounds, vectorSource, true, 2, color, "", textColor);
		}
	}

	// WEST MARKERS

	if (v["WestDiff"]) {
		let numCrops = v["WestDiff"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			let hue = 180 - Math.floor(180 * ((v["WEST"] - mindiff) / (maxdiff - mindiff)));
			let rgbColor = hsvToRgb(hue / 360, 1, 1);
			let color = fullColorHex(rgbColor.r, rgbColor.g, rgbColor.b);
			let yStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
			let yEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;
			let bounds = [
				[(x) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
				[(x) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
				[(x + (margin)) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
				[(x + (margin)) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
				[(x) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)]
			];
			drawRectangle(bounds, vectorSource, true, 2, color, "", textColor);
		}
	}

	// EAST MARKERS
	if (v["EastDiff"]) {
		let numCrops = v["EastDiff"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			let hue = 180 - Math.floor(180 * ((v["EAST"] - mindiff) / (maxdiff - mindiff)));
			let rgbColor = hsvToRgb(hue / 360, 1, 1);
			let color = fullColorHex(rgbColor.r, rgbColor.g, rgbColor.b);
			let yStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
			let yEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;
			let bounds = [
				[(x + 1) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
				[(x + 1) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
				[(x + 1 - (margin)) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
				[(x + 1 - (margin)) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
				[(x + 1) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)]
			];
			drawRectangle(bounds, vectorSource, true, 2, color, "", textColor);
		}
	}

	// SOUTH MARKERS
	if (v["SouthDiff"]) {
		let numCrops = v["SouthDiff"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			let hue = 180 - Math.floor(180 * ((v["SOUTH"] - mindiff) / (maxdiff - mindiff)));
			let rgbColor = hsvToRgb(hue / 360, 1, 1);
			let color = fullColorHex(rgbColor.r, rgbColor.g, rgbColor.b);
			let xStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
			let xEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;
			let bounds = [
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y + (margin)) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y + (margin)) * (slideData.tile_height * slideData.uperpixel)],
				[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y) * (slideData.tile_height * slideData.uperpixel)]
			];
			drawRectangle(bounds, vectorSource, true, 2, color, "", textColor);
		}
	}
}

export const plotFftRatioFourDTextUnit = (v, slideData, x, y, margin, sizeOfMarker, vectorSource, ratioThresh,
										  textColor, textSize) => {

	y = slideData.y_fields - y - 1;

	if (v["NORTH"]) {
		let numCrops = v["NORTH"].length;
		for (let crop = 0; crop < v["NORTH"].length; crop++) {
			let cssLabel = tileViewerPlotColors.fftRatioCleanColor;
			if (v['NORTH'][crop] <= ratioThresh * 1000) {
				cssLabel = tileViewerPlotColors.fftRatioStrongColor;
			}
			if (v["NORTH"][crop])
				drawLine((x + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_width * slideData.uperpixel),
					(y + 1) * (slideData.tile_height * slideData.uperpixel), 1, vectorSource, v["NORTH"][crop],
					1, cssLabel, "", textColor, textSize);
		}
	}

	if (v["WEST"]) {
		let numCrops = v["WEST"].length;
		for (let crop = 0; crop < v["WEST"].length; crop++) {
			let cssLabel = tileViewerPlotColors.fftRatioCleanColor;
			if (v['WEST'][numCrops - 1 - crop] <= ratioThresh * 1000) {
				cssLabel = tileViewerPlotColors.fftRatioStrongColor;
			}
			if (v["WEST"][numCrops - 1 - crop])
				drawLine(x * (slideData.tile_width * slideData.uperpixel),
					(y + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_height * slideData.uperpixel),
					1, vectorSource, v["WEST"][numCrops - 1 - crop], 1, cssLabel, "",
					textColor, textSize);
		}
	}
}

export const transformFFTFourDData = (data, slideData) => {
	let maxdiff = Number.MIN_VALUE;
	let mindiff = Number.MAX_VALUE;
	for(let imageKey in data) {
		let x = parseInt(imageKey.split('y')[0].split('x')[1]);
		let y = parseInt(imageKey.split('y')[1].split('.' + slideData.img_type)[0]);

		// Checking North diff
		let newX = x;
		let newY = y-1;
		let newkey = "x" + newX + "y" + newY + ".jpg";
		if (newX >= 0 && newY >= 0 && newX < slideData.x_fields && newY < slideData.y_fields && data[newkey]) {
			let diffs = []
			if (data[imageKey]["NORTH"] && data[newkey]["SOUTH"]) {
				for (let crop = 0; crop < data[imageKey]["NORTH"].length; crop++) {
					let diff = Math.abs(data[imageKey]["NORTH"][crop] - data[newkey]["SOUTH"][crop]);
					if (diff > maxdiff) {
						maxdiff = diff;
					}
					if (diff < mindiff) {
						mindiff = diff;
					}
					diffs.push(diff);
				}
			}
			data[imageKey]["NorthDiff"] = diffs;
			data[newkey]["SouthDiff"] = diffs;
		}

		// Checking West diff
		newX = x -1;
		newY = y;
		newkey = "x" + newX + "y" + newY + ".jpg";
		if (newX >= 0 && newY >= 0 && newX < slideData.x_fields && newY < slideData.y_fields && data[newkey]) {
			let diffs = []
			if (data[imageKey]["WEST"] && data[newkey]["EAST"]) {
				for (let crop = 0; crop < data[imageKey]["WEST"].length; crop++) {
					let diff = Math.abs(data[imageKey]["WEST"] - data[newkey]["EAST"]);
					if (diff > maxdiff) {
						maxdiff = diff;
					}
					if (diff < mindiff) {
						mindiff = diff;
					}
					diffs.push(diff);
				}
			}
			data[imageKey]["WestDiff"] = diffs;
			data[newkey]["EastDiff"] = diffs;
		}
	}
	return [data, mindiff, maxdiff];
}

export const plotFftRatioFourDUnit = (v, slideData, x, y, margin, sizeOfMarker, vectorSource, ratioThreshL1, ratioThreshL2, type) => {
	y = slideData.y_fields - y - 1;
	if (type === "V") {
		if (v["NORTH"]) {
			let numCrops = v["NORTH"].length;
			for (let crop = 0; crop < v["NORTH"].length; crop++) {
				let color = "#00000050";
				let isGrey = true;
				if (v['NORTH'][crop] <= ratioThreshL2 * 1000) {
					color = "#FFA50080";
					isGrey = false;
				}
				if (v['NORTH'][crop] <= ratioThreshL1 * 1000) {
					color = "#FF000080"
					isGrey = false;
				}

				let xStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
				let xEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;

				// NORTH MARKERS
				let bounds = [
					[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y - (margin / 2) + 1) * (slideData.tile_height * slideData.uperpixel)],
					[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y - (margin / 2) + 1) * (slideData.tile_height * slideData.uperpixel)],
					[(x + xEnd) * (slideData.tile_width * slideData.uperpixel), (y + (margin / 2) + 1) * (slideData.tile_height * slideData.uperpixel)],
					[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y + (margin / 2) + 1) * (slideData.tile_height * slideData.uperpixel)],
					[(x + xStart) * (slideData.tile_width * slideData.uperpixel), (y - (margin / 2) + 1) * (slideData.tile_height * slideData.uperpixel)]
				];
				if (!isGrey) {
					drawRectangle(bounds, vectorSource, true, 2, color);
				}
			}
		}
	}
	if (type === "H") {
		if (v["WEST"]) {
			let numCrops = v["WEST"].length;
			for (let crop = 0; crop < v["WEST"].length; crop++) {
				let color = "#00000050";
				let isGrey = true;
				if (v['WEST'][numCrops - 1 - crop] <= ratioThreshL2 * 1000) {
					color = "#FFA50080";
					isGrey = false;
				}
				if (v['WEST'][numCrops - 1 - crop] <= ratioThreshL1 * 1000) {
					color = "#FF000080";
					isGrey = false;
				}

				let yStart = 0.1 + crop * (0.8 / numCrops) + 0.1;
				let yEnd = 0.1 + (crop + 1) * (0.8 / numCrops) - 0.1;

				// WEST MARKERS
				let bounds = [
					[(x - (margin / 2)) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
					[(x - (margin / 2)) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
					[(x + (margin / 2)) * (slideData.tile_width * slideData.uperpixel), (y + yEnd) * (slideData.tile_height * slideData.uperpixel)],
					[(x + (margin / 2)) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)],
					[(x - (margin / 2)) * (slideData.tile_width * slideData.uperpixel), (y + yStart) * (slideData.tile_height * slideData.uperpixel)]
				];
				if (!isGrey) {
					drawRectangle(bounds, vectorSource, true, 2, color);
				}

			}
		}
	}
}

export const plotCounties = (islandsData, slideMetaData, slideData, vectorSource, takeNumber, textColor) => {
	let index = 0;
	for (let i = 0; i < islandsData.length; i++) {
		// only plot counties from given takeNimber
		if (islandsData[i]["islandKey"]["takeNumber"] === takeNumber) {
			for (let idx = 0; idx < islandsData[i]["counties"].length; idx++) {

				const countyBoundary = islandsData[i]["counties"][idx]["countyBoundary"];
				let rectStartPoint = imagetoCanvasPosHelper(slideMetaData, slideData, slideData.x_fields - countyBoundary["x"], countyBoundary["y"]);
				let rectEndPoint = imagetoCanvasPosHelper(slideMetaData, slideData,
					slideData.x_fields - countyBoundary["x"] - countyBoundary["width"],
					countyBoundary["y"] + countyBoundary["height"]);
				let bounds = [
					[rectStartPoint.x, rectStartPoint.y],
					[rectEndPoint.x, rectStartPoint.y],
					[rectEndPoint.x, rectEndPoint.y],
					[rectStartPoint.x, rectEndPoint.y],
					[rectStartPoint.x, rectStartPoint.y]
				];
				index = (index + 1) % 4;
				drawRectangle(bounds, vectorSource, false, 4, TileViewerConstants.BLUE[index],
					"", textColor);
			}
		}
	}
}

export const plotStitchingErrorTop = (error, maxScore, minScore, slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource, textColor, textSize) => {
	// render stitching error metric for the field at (xHori, yVerti) and its top neighbour (xHori, yVerti - 1)
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;

	yVerti = slideData.y_fields - yVerti - 1;

	let txtColor = "#00d775";

	const northBounds = getNorthBounds(xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns);
	let stroke = 2;

	let colorScore = 180 - Math.floor(180 * ((error - minScore) / (maxScore - minScore)));
	colorScore = Math.floor(180 * ((error - minScore) / (maxScore - minScore)));
	let {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
	const opacity = 0.9;
	const color = `rgba(${r},${g},${b},${opacity})`;

	let title = `${error}`;
	drawRectangle(northBounds, vectorSource, true, stroke, color, "", textColor);
	drawLine((xHori + 0.5) * tile_width_microns, (yVerti + 1) * tile_height_microns, 1, vectorSource,
			title, 1, txtColor, "", textColor, textSize);
}

export const plotStitchingErrorLeft = (error, maxScore, minScore, slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource, textColor, textSize) => {
	// render stitching error metric for the field at (xHori, yVerti) and its left neighbour (xHori - 1, yVerti)
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;

	yVerti = slideData.y_fields - yVerti - 1;

	let txtColor = "#00d775";

	const westBounds = getWestBounds(xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns);
	let stroke = 2;

	let colorScore = 180 - Math.floor(180 * ((error - minScore) / (maxScore - minScore)));
	colorScore = Math.floor(180 * ((error - minScore) / (maxScore - minScore)));
	let {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
	const opacity = 0.9;
	const color = `rgba(${r},${g},${b},${opacity})`;

	let title = `${error}`;
	drawRectangle(westBounds, vectorSource, true, stroke, color, "", textColor);
	drawLine(xHori * tile_width_microns, (yVerti + 0.5) * tile_height_microns, 1,
			vectorSource, title, 0.25, txtColor, "", textColor, textSize);
}

export const plotDisagreementv2Unit = (disagreement, disagreementL2NormMax, disagreementL2NormMin,
									   slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource, textColor) => {
	// render `disagreement` at the field located at (xHori, yVerti)
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;

	yVerti = slideData.y_fields - yVerti - 1;

	// NORTH MARKERS
	drawNorthMarkers(xHori, yVerti, sizeOfMarker, margin,
		tile_width_microns, tile_height_microns, disagreement, vectorSource,
		disagreementL2NormMax, disagreementL2NormMin, textColor);

	// WEST MARKERS
	drawWestMarkers(xHori, yVerti, sizeOfMarker, margin,
		tile_width_microns, tile_height_microns, disagreement, vectorSource,
		disagreementL2NormMax, disagreementL2NormMin, textColor);
}

export const plotDisagreementTextv2Unit = (disagreement, slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource,
										   textColor, textSize) => {
	// render `disagreement` at the field located at (xHori, yVerti)
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;

	yVerti = slideData.y_fields - yVerti - 1;

	// NORTH MARKERS
	drawNorthMarkersText(xHori, yVerti, sizeOfMarker, margin,
		tile_width_microns, tile_height_microns, disagreement, vectorSource, textColor, textSize);

	// WEST MARKERS
	drawWestMarkersText(xHori, yVerti, sizeOfMarker, margin,
		tile_width_microns, tile_height_microns, disagreement, vectorSource, textColor, textSize);
}

export const drawNorthMarkersText = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns,
									 fieldStitchResult, vectorSource, textColor, textSize) => {

	if (fieldStitchResult["noActualTopTxTy"] && fieldStitchResult["noActualTopTxTy"]) {
		return;
	}

	let txtColor = "#00d775";

	if (fieldStitchResult["computedTop"]) {
		const disagreementTop = fieldStitchResult["disagreementTop"];
		const l1Sum = Math.abs(disagreementTop["deltaTx"]) + Math.abs(disagreementTop["deltaTy"]);
		if (l1Sum > 0) {
			txtColor = "#666666";
		}
	}
	if (fieldStitchResult["computedTop"]) {
		if (fieldStitchResult["computedTop"]["ty"] === 0) {
			txtColor = "#00d775";
		}
	}
	if (fieldStitchResult["actualTop"] && fieldStitchResult["actualTop"]["ty"] === 0) {
		txtColor = "#00a532";
	}

	let disagreementTop = fieldStitchResult["disagreementTop"];
	if (Math.abs(disagreementTop["deltaTx"]) > 1000 || Math.abs(disagreementTop["deltaTy"]) > 1000) {
		return;
	}

	if (fieldStitchResult["computedTop"]) {
		let title = "Δtx=" + disagreementTop["deltaTx"] + "_Δty=" + disagreementTop["deltaTy"];
		if (disagreementTop["deltaTx"] === 0 && disagreementTop["deltaTy"] === 0) {
			return; // DON't draw
		}
		if (fieldStitchResult["computedTop"]["ty"] === 0) {
			title = "no Δtx Δty";
		}
		if (fieldStitchResult["actualTop"]["ty"] === 0) {
			title = "actual tx ty 0";
		}
		drawLine((xHori + 0.5) * tile_width_microns, (yVerti + 1) * tile_height_microns, 1, vectorSource,
			title, 1, txtColor, "", textColor, textSize);
	}
}

export const drawWestMarkersText = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns,
									fieldStitchResult, vectorSource, textColor, textSize) => {
	if (fieldStitchResult["noActualLeftTxTy"] && fieldStitchResult["noActualLeftTxTy"]) {
		return;
	}

	let txtColor = "#00d775";

	if (fieldStitchResult["computedLeft"]) {
		const disagreementLeft = fieldStitchResult["disagreementLeft"];
		const l1Sum = Math.abs(disagreementLeft["deltaTx"]) + Math.abs(disagreementLeft["deltaTy"]);
		if (l1Sum > 0) {
			txtColor = "#666666";
		}
	}
	if (fieldStitchResult["computedLeft"]) {
		if (fieldStitchResult["computedLeft"]["tx"] === 0) {
			txtColor = "#00d775";
		}
	}
	if (fieldStitchResult["actualLeft"] && fieldStitchResult["actualLeft"]["tx"] === 0) {
		txtColor = "#00a532";
	}

	let disagreementLeft = fieldStitchResult["disagreementLeft"];
	if (Math.abs(disagreementLeft["deltaTx"]) > 1000 || Math.abs(disagreementLeft["deltaTy"]) > 1000) {
		return;
	}

	if (fieldStitchResult["computedLeft"]) {
		//const disagreementLeft = fieldStitchResult["disagreementLeft"];
		let title = "Δtx=" + disagreementLeft["deltaTx"] + "_Δty=" + disagreementLeft["deltaTy"];
		if (disagreementLeft["deltaTx"] === 0 && disagreementLeft["deltaTy"] === 0) {
			return; // DON't draw
		}
		if (fieldStitchResult["computedLeft"]["tx"] === 0) {
			title = "no Δtx Δty";
		}
		if (fieldStitchResult["actualLeft"]["tx"] === 0) {
			title = "actual tx ty 0";
		}
		drawLine(xHori * tile_width_microns, (yVerti + 0.5) * tile_height_microns, 1,
			vectorSource, title, 0.25, txtColor, "", textColor, textSize);
	}
}

export const getInvertedColor = (maxVal, minVal, val) => {
	const colorScore = 180 - Math.floor(180 * ((val - minVal) / (maxVal - minVal)));
	let {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
	return `rgb(${r},${g},${b},0.1)`;
}

export const getNorthBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns) =>
	[
		[(xHori + sizeOfMarker) * (tile_width_microns), (yVerti - (margin / 2) + 1) * (tile_height_microns)],
		[(xHori + (1 - sizeOfMarker)) * (tile_width_microns), (yVerti - (margin / 2) + 1) * (tile_height_microns)],
		[(xHori + (1 - sizeOfMarker)) * (tile_width_microns), (yVerti + (margin / 2) + 1) * (tile_height_microns)],
		[(xHori + sizeOfMarker) * (tile_width_microns), (yVerti + (margin / 2) + 1) * (tile_height_microns)],
		[(xHori + sizeOfMarker) * (tile_width_microns), (yVerti - (margin / 2) + 1) * (tile_height_microns)]
	];

export const getWestBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns) =>
	[
		[(xHori - (margin / 2)) * (tile_width_microns), (yVerti + sizeOfMarker) * (tile_height_microns)],
		[(xHori - (margin / 2)) * (tile_width_microns), (yVerti + (1 - sizeOfMarker)) * (tile_height_microns)],
		[(xHori + (margin / 2)) * (tile_width_microns), (yVerti + (1 - sizeOfMarker)) * (tile_height_microns)],
		[(xHori + (margin / 2)) * (tile_width_microns), (yVerti + sizeOfMarker) * (tile_height_microns)],
		[(xHori - (margin / 2)) * (tile_width_microns), (yVerti + sizeOfMarker) * (tile_height_microns)]
	];

export const plotDisagreementsHelper = (stitchResultv2, slideData, vectorSource, textColor) => {
	const size = 3;
	const margin = size / 10;
	const sizeOfMarker = 0.2;

	let disagreementL2NormMax = 0;
	let disagreementL2NormMin = 100000000; // some large number
	for (const key in stitchResultv2) {
		// sample key is "x9y32.jpg"
		const disagreement = stitchResultv2[key];
		const bothComputedDeltaZero = disagreement["computedTop"]["tx"] === 0
			&& disagreement["computedTop"]["ty"] === 0;
		if (disagreement["noActualTopTxTy"] && disagreement["noActualTopTxTy"] === false && !bothComputedDeltaZero) {
			const disagreementTop = disagreement["disagreementTop"];
			const deltaTxSq = disagreementTop["deltaTx"] * disagreementTop["deltaTx"];
			const deltaTySq = disagreementTop["deltaTy"] * disagreementTop["deltaTy"];
			const l2norm = Math.sqrt(deltaTxSq + deltaTySq);
			if (l2norm < 1000 && l2norm > disagreementL2NormMax)
				disagreementL2NormMax = l2norm;
			if (l2norm < 1000 && l2norm < disagreementL2NormMin)
				disagreementL2NormMin = l2norm;
		}
		const bothComputedDeltaZero2 = disagreement["computedLeft"]["tx"] === 0
			&& disagreement["computedLeft"]["ty"] === 0;
		if (disagreement["noActualLeftTxTy"] && disagreement["noActualLeftTxTy"] === false && !bothComputedDeltaZero2) {
			const disagreementLeft = disagreement["disagreementLeft"];
			const deltaTxSq = disagreementLeft["deltaTx"] * disagreementLeft["deltaTx"];
			const deltaTySq = disagreementLeft["deltaTy"] * disagreementLeft["deltaTy"];
			const l2norm = Math.sqrt(deltaTxSq + deltaTySq);
			if (l2norm < 1000 && l2norm > disagreementL2NormMax)
				disagreementL2NormMax = l2norm;
			if (l2norm < 1000 && l2norm < disagreementL2NormMin)
				disagreementL2NormMin = l2norm;
		}
	}

	for(const key in stitchResultv2) {
		// sample key is "x9y32.jpg"
		const xHori = parseInt(key.split('y')[0].split('x')[1]);
		const yVerti = parseInt(key.split('y')[1].split('.' + slideData.img_type)[0]);
		const disagreement = stitchResultv2[key];
		plotDisagreementv2Unit(disagreement, disagreementL2NormMax, disagreementL2NormMin,
			slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource, textColor);
		plotDisagreementTextv2Unit(disagreement, slideData, xHori, yVerti, margin, sizeOfMarker, vectorSource, textColor);
	}
}

export const plotStitchGraphHelper = (allPaths, slideData, vectorSource) => {
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;

	if (allPaths && (allPaths.length === 0 || Object.keys(allPaths).length === 0)) {
		return;
	}

	// draw arrows from source -> target for `entirePath`
	const tileCounts = allPaths.map(function(ccPath) {return 1+ccPath.length;})
		.filter(function(tileCount) {return tileCount > 1;});
	let maxTileCount2 = Math.max(...tileCounts);
	let minTileCount2 = Math.min(...tileCounts);
	for (const ccPath of allPaths) {
		const tileCount = 1 + ccPath.length;
		const colorScore = 180 - Math.floor(180 * ((tileCount - minTileCount2)
			/ (maxTileCount2 - minTileCount2)));
		const {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
		const ccColor = `rgba(${r},${g},${b},0.4)`;

		for (const traversalUnit of ccPath) {
			const xHori = traversalUnit["source"]["col"];
			let yVerti = traversalUnit["source"]["row"];
			yVerti = slideData.y_fields - yVerti - 1;
			const direction = getArrowDirection(traversalUnit);
			// DRAW
			drawArrowInDirection(xHori, yVerti, tile_width_microns, tile_height_microns,
				direction, vectorSource, ccColor);
		}
	}
}

export const plotStitchGraphStartPositions = (ccReducedMetas, slideData, vectorSource) => {
	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;
	if (ccReducedMetas && (ccReducedMetas.length === 0 || Object.keys(ccReducedMetas).length === 0)) {
		return;
	}

	const tileCounts1 = ccReducedMetas.map(function(ccMeta) {return ccMeta["tileCount"];})
		.filter(function(tileCount) {return tileCount > 1;});
	let maxTileCount = Math.max(...tileCounts1);
	let minTileCount = Math.min(...tileCounts1);
	// draw squares at all `ccReducedMetas`
	for (const ccMeta of ccReducedMetas) {
		const position = ccMeta["ccStartPos"];
		const tileCount = ccMeta["tileCount"];
		const colorScore = 180 - Math.floor(180 * ((tileCount - minTileCount) / (maxTileCount - minTileCount)));
		const {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
		let opacity = 0.7;
		if(tileCount < 2) {
			opacity = 0.1;
		}
		const ccColor = `rgba(${r},${g},${b},${opacity})`;

		const xHori = position["col"];
		let yVerti = position["row"];
		yVerti = slideData.y_fields - yVerti - 1;

		const size = 5;
		const margin = size / 10;
		const sizeOfMarker = 0.25;
		let stroke = 1;
		const centerBounds = getTileCenterBounds(xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns);
		drawRectangle(centerBounds, vectorSource, true, stroke, ccColor);
	}
}

export const getTileCenterBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns) =>
	// 5 pts to close a rect, convention: start at top left, move clockwise
	// note: start end end pts are same
	[
		[(xHori + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)]
	];

export const drawArrowInDirection = (xHori, yVerti, tile_width_microns, tile_height_microns, direction, vectorSource, ccColor) => {
	const size = 0.5;
	const margin = size / 10;
	const sizeOfArrow = 0.2;
	// let color = "rgba(0, 185, 123, 0.6)";
	// let arrowColor = "rgba(0, 235, 0, 0.8)";
	let stroke = 1;
	if (direction === StitchDirection.LEFT || direction === StitchDirection.RIGHT) {
		const arrowBodyBounds = getLRArrowBodyBounds(xHori, yVerti, sizeOfArrow, margin, tile_width_microns, tile_height_microns, direction);
		// DRAW
		drawRectangle(arrowBodyBounds, vectorSource, true, stroke, ccColor);
		let arrowHeadBounds = getLRArrowHeadBounds(xHori, yVerti, 2*sizeOfArrow, 3*margin, tile_width_microns, tile_height_microns, direction);
		// DRAW
		drawTriangle(arrowHeadBounds, vectorSource, true, 2, ccColor);
	} else if (direction === StitchDirection.UP || direction === StitchDirection.DOWN) {
		const arrowBodyBounds = getUDArrowBodyBounds(xHori, yVerti, sizeOfArrow, margin, tile_width_microns, tile_height_microns, direction);
		// DRAW
		drawRectangle(arrowBodyBounds, vectorSource, true, stroke, ccColor);
		let arrowHeadBounds = getUDArrowHeadBounds(xHori, yVerti, 0.5*sizeOfArrow, 3*margin, tile_width_microns, tile_height_microns, direction);
		// DRAW
		drawTriangle(arrowHeadBounds, vectorSource, true, 2, ccColor);
	}
}

export const getArrowDirection = (traversalUnit) => {
	const xHori = traversalUnit["source"]["col"];
	const yVerti = traversalUnit["source"]["row"];
	const targetXHori = traversalUnit["target"]["col"];
	const targetYverti = traversalUnit["target"]["row"];
	if (targetXHori < xHori) { return  StitchDirection.LEFT; }
	if (targetXHori > xHori) { return  StitchDirection.RIGHT; }
	if (targetYverti < yVerti) { return  StitchDirection.UP; }
	if (targetYverti > yVerti) { return  StitchDirection.DOWN; }
	return StitchDirection.UNKNOWN_DIRECTION;
}

export const drawNorthMarkers = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns,
								 fieldStitchResult, vectorSource, maxl2norm, minl2norm, textColor) => {
	if (fieldStitchResult["noActualTopTxTy"] && fieldStitchResult["noActualTopTxTy"]) {
		return;
	}

	let color = "rgba(85, 85, 23, 0.2)";
	let stroke = 0;
	const northBounds = getNorthBounds(xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns);
	let opacity = 0.9;

	if (fieldStitchResult["computedTop"]) {
		const disagreementTop = fieldStitchResult["disagreementTop"];
		const deltaTxSq = disagreementTop["deltaTx"] * disagreementTop["deltaTx"];
		const deltaTySq = disagreementTop["deltaTy"] * disagreementTop["deltaTy"];
		const l2Sum = Math.sqrt(deltaTxSq + deltaTySq);
		opacity = 0.9;
		stroke = 2;

		const MIN_VAL = Math.sqrt(2) + 0.01;
		// const cappedL2Sum = Math.min(l2Sum, MAX_VAL);
		const colorScore = 180 - Math.floor(180 * ((l2Sum - minl2norm) / (maxl2norm - minl2norm)));
		let {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);
		if (l2Sum < MIN_VAL) {
			opacity = 0; // hidden
		}
		color = `rgba(${r},${g},${b},${opacity})`;
		// color = `rgba(${rByte}, 0, ${bByte}, ${opacity})`;
	}
	if (fieldStitchResult["computedTop"]) {
		if (fieldStitchResult["computedTop"]["ty"] === 0) {
			color = "rgba(100, 100, 100, 0.2)";
		}
	}
	if (fieldStitchResult["actualTop"] && fieldStitchResult["actualTop"]["ty"] === 0) {
		color = "rgba(100, 100, 100, 0.2)";
	}

	let disagreementTop = fieldStitchResult["disagreementTop"];
	if (Math.abs(disagreementTop["deltaTx"]) > 1000 || Math.abs(disagreementTop["deltaTy"]) > 1000) {
		return;
	}

	if (opacity > 0) {
		drawRectangle(northBounds, vectorSource, true, stroke, color, "", textColor);
	}
}

export const drawWestMarkers = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns,
								fieldStitchResult, vectorSource, maxl2norm, minl2norm, textColor) => {
	if (fieldStitchResult["noActualLeftTxTy"] && fieldStitchResult["noActualLeftTxTy"]) {
		return;
	}
	let color = "rgba(85, 85, 23, 0.2)";
	const westBounds = getWestBounds(xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns);
	let stroke = 0;
	let opacity = 0.9;

	if (fieldStitchResult["computedLeft"]) {
		const disagreementLeft = fieldStitchResult["disagreementLeft"];

		const deltaTxSq = disagreementLeft["deltaTx"] * disagreementLeft["deltaTx"];
		const deltaTySq = disagreementLeft["deltaTy"] * disagreementLeft["deltaTy"];
		const l2Sum = Math.sqrt(deltaTxSq + deltaTySq);
		opacity = 0.9;
		stroke = 2;

		const MIN_VAL = Math.sqrt(2) + 0.01;
		// const cappedL2Sum = Math.min(l2Sum, MAX_VAL);
		const colorScore = 180 - Math.floor(180 * ((l2Sum - minl2norm) / (maxl2norm - minl2norm)));
		let {r, g, b} = hsvToRgb(colorScore / 360, 1, 1);

		if (l2Sum < MIN_VAL) {
			opacity = 0; // hidden
		}
		color = `rgba(${r},${g},${b},${opacity})`;
	}
	if (fieldStitchResult["computedLeft"] && fieldStitchResult["computedLeft"]["tx"] === 0) {
		color = "rgba(100, 100, 100, 0.2)";
	}
	if (fieldStitchResult["actualLeft"] && fieldStitchResult["actualLeft"]["tx"] === 0) {
		color = "rgba(100, 100, 100, 0.2)";
	}

	let disagreementLeft = fieldStitchResult["disagreementLeft"];
	if (Math.abs(disagreementLeft["deltaTx"]) > 1000 || Math.abs(disagreementLeft["deltaTy"]) > 1000) {
		return;
	}

	if (opacity > 0) {
		drawRectangle(westBounds, vectorSource, true, stroke, color, "", textColor);
	}
}

export const plotTileMotionHelper = (tileMotionList, slideData, vectorSource, textColor) => {
	if (tileMotionList && (tileMotionList.length === 0 || Object.keys(tileMotionList).length === 0)) {
		return;
	}

	const tile_width_microns = slideData.tile_width * slideData.uperpixel;
	const tile_height_microns = slideData.tile_height * slideData.uperpixel;
	const ccColor = `rgba(255, 255, 255, 0.8)`;

	for (const tileMotion of tileMotionList) {
		const xMotion = tileMotion["xmotion"];
		const yMotion = tileMotion["ymotion"];
		const xHori = tileMotion["fieldPosition"]["col"];
		let yVerti = tileMotion["fieldPosition"]["row"];
		yVerti = slideData.y_fields - yVerti - 1;

		const size = 0.5;
		const margin = size / 10;
		const sizeOfArrow = 0.35;
		let stroke = 1;

		if (Math.abs(xMotion) > 0) {
			const direction = xMotion > 0 ? StitchDirection.LEFT : StitchDirection.RIGHT;
			const arrowBodyBounds = getLRArrowBodyBoundsTileMotion(xHori, yVerti, sizeOfArrow, margin, tile_width_microns, tile_height_microns, direction);
			// DRAW
			drawRectangle(arrowBodyBounds, vectorSource, true, stroke, ccColor, "", textColor);
			let arrowHeadBounds = getLRArrowHeadBoundsTileMotion(xHori, yVerti, 0.45, 3 * margin, tile_width_microns, tile_height_microns, direction);
			// DRAW
			drawTriangle(arrowHeadBounds, vectorSource, true, 2, ccColor, "", textColor);
			// Write xMotion value
			let xShift = 0.125;
			if (xMotion < 0) {
				xShift = 0.875;
			}
			drawLine((xHori + xShift) * tile_width_microns, (yVerti + 0.5125) * tile_height_microns, 1,
				vectorSource, Math.abs(xMotion).toString(), 1, "#00d775", "", textColor);
		}
		if (Math.abs(yMotion) > 0) {
			const direction = yMotion > 0 ? StitchDirection.UP : StitchDirection.DOWN;
			const arrowBodyBounds = getUDArrowBodyBoundsTileMotion(xHori, yVerti, sizeOfArrow, margin, tile_width_microns, tile_height_microns, direction);
			// DRAW
			drawRectangle(arrowBodyBounds, vectorSource, true, stroke, ccColor, "", textColor);
			let arrowHeadBounds = getUDArrowHeadBoundsTileMotion(xHori, yVerti, 0.075, 3 * margin, tile_width_microns, tile_height_microns, direction);
			// DRAW
			drawTriangle(arrowHeadBounds, vectorSource, true, 2, ccColor, "", textColor);
			// Write yMotion value
			let yShift = 0.9;
			if (yMotion < 0) {
				yShift = 0.125;
			}
			drawLine((xHori + 0.5) * tile_width_microns, (yVerti + yShift) * tile_height_microns, 1,
				vectorSource, Math.abs(yMotion).toString(), 1, "#00d775", "", textColor);
		}
	}
}

export const getLRArrowBodyBoundsTileMotion = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	// 5 pts to close a rect, convention: start at top left, move clockwise
	// note: start end end pts are same
	let xShift = -0.25;
	if (direction === StitchDirection.RIGHT) {
		xShift = 0.25;
	}
	return [
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker) + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker) + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)]
	];
}

export const getLRArrowHeadBoundsTileMotion = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	let xShift = -0.4;
	if (direction === StitchDirection.RIGHT) { xShift = 0.4; }
	let bounds;
	if (direction === StitchDirection.LEFT)  {
		bounds = [
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ]
		];
	} else if (direction === StitchDirection.RIGHT) {
		bounds = [
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ],
			[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ]
		];
	}
	return bounds;
}

export const getUDArrowBodyBoundsTileMotion = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	// 5 pts to close a rect, convention: start at top left, move clockwise
	// note: start end end pts are same
	let yShift = -0.3;
	if (direction === StitchDirection.DOWN) { yShift = -0.7; }
	return [
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))],
		[(xHori + (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))],
		[(xHori + (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + (1 - sizeOfMarker) + 0.5) * (tile_height_microns))],
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + (1 - sizeOfMarker) + 0.5) * (tile_height_microns))],
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))]
	];
}

export const getUDArrowHeadBoundsTileMotion = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	let yShift = 0.4;
	if (direction === StitchDirection.DOWN) { yShift=-0.4; }
	let bounds;
	if (direction === StitchDirection.UP)  {
		bounds = [
			[(xHori - margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori - margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))]
		];
	} else if (direction === StitchDirection.DOWN) {
		bounds = [
			[(xHori + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + margin/2 + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori - margin/2 + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))]
		];
	}
	return bounds;
}

export const getCountyKey = (islandKey, countyId) => {
	// islandKey is dict{"takeNumber": int, "islandId": int}
	// countyId is int
	return `t${islandKey["takeNumber"]}_i${islandKey["islandId"]}_c${countyId}`;
}

const getLRArrowBodyBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	// 5 pts to close a rect, convention: start at top left, move clockwise
	// note: start end end pts are same
	let xShift = -0.45;
	if (direction === StitchDirection.RIGHT) {
		xShift = 0.55;
	}
	return [
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker) + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + (1 - sizeOfMarker) + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)],
		[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5 * tile_height_microns)]
	];
}

const getLRArrowHeadBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	let xShift = -0.8;
	if (direction === StitchDirection.RIGHT) { xShift = 0.9; }
	let bounds;
	if (direction === StitchDirection.LEFT)  {
		bounds = [
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ]
		];
	} else if (direction === StitchDirection.RIGHT) {
		bounds = [
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ],
			[(xHori + sizeOfMarker + xShift) * (tile_width_microns), ((yVerti + (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + (1 - sizeOfMarker)) * (tile_width_microns), ((yVerti + 1) * (tile_height_microns)) - (0.5*tile_height_microns)],
			[(xHori + xShift + sizeOfMarker) * (tile_width_microns), ((yVerti - (margin / 2) + 1) * (tile_height_microns)) - (0.5*tile_height_microns) ]
		];
	}
	return bounds;
}

const getUDArrowBodyBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	// 5 pts to close a rect, convention: start at top left, move clockwise
	// note: start end end pts are same
	let yShift = 0;
	if (direction === StitchDirection.DOWN) { yShift = -1.1; }
	return [
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))],
		[(xHori + (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))],
		[(xHori + (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + (1 - sizeOfMarker) + 0.5) * (tile_height_microns))],
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + (1 - sizeOfMarker) + 0.5) * (tile_height_microns))],
		[(xHori - (margin / 2) + 0.5) * (tile_width_microns), ((yVerti + yShift + sizeOfMarker + 0.5) * (tile_height_microns))]
	];
}

const getUDArrowHeadBounds = (xHori, yVerti, sizeOfMarker, margin, tile_width_microns, tile_height_microns, direction) => {
	let yShift = 0.9;
	if (direction === StitchDirection.DOWN) { yShift=-1.0; }
	let bounds;
	if (direction === StitchDirection.UP)  {
		bounds = [
			[(xHori - margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori - margin/2 + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))]
		];
	} else if (direction === StitchDirection.DOWN) {
		bounds = [
			[(xHori + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + margin / 2 + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori - margin / 2 + 0.5) * (tile_width_microns), ((yVerti + sizeOfMarker + yShift + 0.5) * (tile_height_microns))],
			[(xHori + 0.5) * (tile_width_microns), ((yVerti - sizeOfMarker + yShift + 0.5) * (tile_height_microns))]
		];
	}
	return bounds;
}

export const plotFftFourDTextUnit = (v, slideData, x, y, margin, sizeOfMarker, vectorSource, textColor, textSize) => {

	y = slideData.y_fields - y - 1;

	// NORTH MARKERS
	let numCrops;

	if (v["NORTH"]) {
		numCrops = v["NORTH"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			drawLine((x + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_width * slideData.uperpixel),
				(y + 1 - (margin / 2)) * (slideData.tile_height * slideData.uperpixel),
				1, vectorSource, Math.round(v["NORTH"][crop] * 100) / 100, 1, "#000000",
				"", textColor, textSize);
		}
	}

	// WEST MARKERS
	if (v["WEST"]) {
		numCrops = v["WEST"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			drawLine((x + (margin / 2)) * (slideData.tile_width * slideData.uperpixel),
				(y + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_height * slideData.uperpixel),
				1, vectorSource, Math.round(v["WEST"][numCrops - crop - 1] * 100) / 100, 1,
				"#000000", "", textColor, textSize);
		}
	}

	// EAST MARKERS
	if (v["EAST"]) {
		numCrops = v["EAST"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			drawLine((x + 1 - (margin / 2)) * (slideData.tile_width * slideData.uperpixel),
				(y + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_height * slideData.uperpixel),
				1, vectorSource, Math.round(v["EAST"][numCrops - crop - 1] * 100) / 100, 1,
				"#000000", "", textColor, textSize);
		}
	}

	// SOUTH MARKERS
	if (v["SOUTH"]) {
		numCrops = v["SOUTH"].length;
		for (let crop = 0; crop < numCrops; crop++) {
			drawLine((x + 0.1 + (0.8 / numCrops) * (crop + 0.5)) * (slideData.tile_width * slideData.uperpixel),
				(y + (margin / 2)) * (slideData.tile_height * slideData.uperpixel),
				1, vectorSource, Math.round(v["SOUTH"][crop] * 100) / 100, 1, "#000000"
				, "", textColor, textSize);
		}
	}
}

export const isMobileViewer = () => document.documentElement.clientHeight <= 500

export const isMobile = () => document.documentElement.clientWidth <= responsiveLayers.MOBILE_WIDTH_LEVEL &&
	(document.documentElement.clientWidth < document.documentElement.clientHeight)

export const makeWaterMarkVisible = () => {
	const el = document.getElementById(componentIds.morphleWatermark);
	if (el) el.classList.add("morphle-watermark-visible");
}

export const makeWaterMarkHidden = () => {
	const el = document.getElementById(componentIds.morphleWatermark);
	if (el) el.classList.remove("morphle-watermark-visible");
}

export const moveScaleLineToLeft = () => {
	const scaleLineControl = document.getElementById(componentIds.scaleLineControl);
	if (scaleLineControl) {
		scaleLineControl.parentElement.style.right = '150px'; 
	}
  }
  
export const restoreScaleLinePosition = () => {
	const scaleLineControl = document.getElementById(componentIds.scaleLineControl);
	if (scaleLineControl) {
		scaleLineControl.parentElement.style.right = null; 
	}
}

/** Hide OL scale line during capture (restored with restoreScaleLineVisibility). */
export const hideScaleLineForCapture = () => {
	const scaleLineControl = document.getElementById(componentIds.scaleLineControl);
	if (!scaleLineControl) return null;
	const prev = scaleLineControl.style.visibility;
	scaleLineControl.style.visibility = "hidden";
	return prev;
};

export const restoreScaleLineVisibility = (prevVisibility) => {
	const scaleLineControl = document.getElementById(componentIds.scaleLineControl);
	if (!scaleLineControl) return;
	scaleLineControl.style.visibility = prevVisibility != null ? prevVisibility : "";
};

export const selectedAnnotationStyle = () => {
	const stylePimary = new Style({
		stroke: new Stroke({
			color: AnnotationsConstants.SELECTED_ANNOTATION.COLOR_PRIMARY,
			width: AnnotationsConstants.SELECTED_ANNOTATION.WIDTH_PRIMARY
		}),
		fill: new Fill({
			color: AnnotationsConstants.SELECTED_ANNOTATION.FILL
		}),
	})

	const styleSecondary = new Style({
		stroke: new Stroke({
			color: AnnotationsConstants.SELECTED_ANNOTATION.COLOR_SECONDARY,
			width: AnnotationsConstants.SELECTED_ANNOTATION.WIDTH_SECONDARY
		})
	})
	return [styleSecondary, stylePimary]
}
